import { ReactComponent as ExclamationCircleFillSVG } from 'assets/icons/exclamationCircleFill.svg';
import { ReactComponent as MicrophoneNotFoundSvg } from 'assets/icons/microphoneNotFound.svg';
import { ReactComponent as MicrophoneMutedSvg } from 'assets/icons/microphoneMuted.svg';
import { ReactComponent as CameraNotFound } from 'assets/icons/cameraNotFound.svg';
import { ReactComponent as CameraMutedSvg } from 'assets/icons/cameraMuted.svg';
import { ReactComponent as MicrophoneSvg } from 'assets/icons/microhpone.svg';
import { ReactComponent as CameraSvg } from 'assets/icons/camera.svg';
import { ReactComponent as PhoneSvg } from 'assets/icons/phone.svg';
import { ReactComponent as EmailSvg } from 'assets/icons/email.svg';
import { ReactComponent as NameSvg } from 'assets/icons/name.svg';
import { ReactComponent as BlurSvg } from 'assets/icons/blur.svg';

import { AvailableDevices, MediaDeviceInfoType, MeetInfo, SelectedDevices, State } from '../types/Video';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import DeviceDropdown from '../shared/DeviceDropdown';
import { PermissionWarning } from '../shared/PermissionWarning';
import { Navbar } from '../shared/Navbar';
import { poweredByLink } from '../shared/variables';
import { formatDate } from 'utils/formatDate';
import { formatTime } from 'utils/formatTime';
import {
  VideoWithControls,
  DeviceSelection,
  PreCallWrapper,
  MeetingDetails,
  VideoWrapper,
  YourDetails,
  Description,
  JoinMeeting,
  MeetingDate,
  PoweredBy,
  PersonSvg,
  Controls,
  Content,
  Middle,
  Center,
  Email,
  Title,
  Phone,
  Cards,
  Right,
  Name,
  Left,
  ToggleButton,
  Illustration,
} from './PreCallStyle';
import { getJoinWindowMs } from './joinWindowConfig';

type PreCallProps = {
  changeCurrentState: (state: State) => void;
  meetInfo: MeetInfo;
  localVideoTrack: MediaStreamTrack | null;
  localBlurredVideoTrack: MediaStreamTrack | null;
  localAudioTrack: MediaStreamTrack | null;
  makeCall: () => void;
  toggleMicrophone: () => void;
  toggleCamera: () => void;
  cameraMuted: boolean;
  microphoneMuted: boolean;
  isVideoBlurred: boolean;
  applyBlurOnPreCallVideo: () => void;
  updateDevice: (device: MediaDeviceInfoType) => void;
  availableDevices?: AvailableDevices;
  selectedDevices?: SelectedDevices;
  isUpgrade?: boolean;
  useVonage?: boolean;
  cameraPermissionDenied?: boolean;
};

export const PreCall = ({
  changeCurrentState,
  meetInfo,
  localVideoTrack,
  localBlurredVideoTrack,
  localAudioTrack,
  makeCall,
  toggleMicrophone,
  toggleCamera,
  cameraMuted,
  microphoneMuted,
  applyBlurOnPreCallVideo,
  updateDevice,
  availableDevices,
  selectedDevices,
  isVideoBlurred,
  isUpgrade,
  useVonage = false,
  cameraPermissionDenied,
}: PreCallProps) => {
  const { t } = useTranslation();
  const [currentDateValue, setCurrentDateValue] = useState<string>(
    new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
    }).format(new Date()),
  );
  const videoRef = useRef<HTMLVideoElement | null>(null);

  useEffect(() => {
    if (!useVonage && videoRef.current) {
      const mediaStream = new MediaStream();

      if (localVideoTrack) {
        mediaStream.addTrack(localVideoTrack);
      }

      if (localAudioTrack) {
        mediaStream.addTrack(localAudioTrack);
      }

      if (mediaStream.getTracks().length > 0) {
        videoRef.current.srcObject = mediaStream;
        videoRef.current.play().catch((err) => {
          console.error('Error playing video:', err);
        });
      } else {
        videoRef.current.srcObject = null;
        if (!cameraMuted && !microphoneMuted) {
          console.warn('No tracks available to display - check camera/microphone permissions');
        }
      }
    }
  }, [localVideoTrack, localAudioTrack, useVonage]);

  const handleDeviceSelect = (device: MediaDeviceInfoType) => {
    updateDevice(device);
  };

  const getMeetState = (startTime: string) => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('debug') === 'true') return 'ongoing';

    const now = new Date(currentDateValue).getTime();
    const meetingStartTime = new Date(startTime);
    meetingStartTime.setSeconds(0, 0);
    const scheduledStart = meetingStartTime.getTime();
    const joinWindowMs = getJoinWindowMs(meetInfo.accountId);
    const joinWindowOpensAt = scheduledStart - joinWindowMs;
    const joinWindowClosesAt = scheduledStart + joinWindowMs;

    if (now < joinWindowOpensAt) {
      return 'notStarted';
    } else if (now > joinWindowClosesAt) {
      return 'expired';
    }
    return 'ongoing';
  };

  const meetState: 'ongoing' | 'notStarted' | 'expired' = getMeetState(meetInfo.startTime) || 'ongoing';

  return (
    <PreCallWrapper>
      <Navbar />
      <Center>
        <Content>
          <VideoWithControls>
            <VideoWrapper $isVideoBlurred={isVideoBlurred}>
              {!useVonage && (
                <>
                  <video ref={videoRef} autoPlay playsInline={true} id={'localVideoPreCall'} muted={true} />
                  <canvas id="localVideoPreCallCanvas" className={'hide'}></canvas>
                  {!localVideoTrack?.enabled && <PersonSvg />}
                </>
              )}
              {useVonage && <div id="localVideoPreCall"></div>}
              <PermissionWarning show={cameraPermissionDenied} />
              <Controls>
                <Left></Left>
                <Middle>
                  <ToggleButton
                    $muted={microphoneMuted}
                    onClick={() => toggleMicrophone()}
                    aria-label={microphoneMuted ? t('microphone.unmute') : t('microphone.mute')}
                  >
                    {availableDevices?.audioInputs.length === 0 ? (
                      <MicrophoneNotFoundSvg />
                    ) : microphoneMuted ? (
                      <MicrophoneMutedSvg />
                    ) : (
                      <MicrophoneSvg />
                    )}
                  </ToggleButton>
                  <ToggleButton
                    $muted={cameraMuted}
                    onClick={() => toggleCamera()}
                    aria-label={cameraMuted ? t('camera.turnOn') : t('camera.turnOff')}
                  >
                    {availableDevices?.videoInputs.length === 0 ? (
                      <CameraNotFound />
                    ) : cameraMuted ? (
                      <CameraMutedSvg />
                    ) : (
                      <CameraSvg />
                    )}
                  </ToggleButton>
                </Middle>
                <Right $isVideoBlurred={isVideoBlurred}>
                  <button
                    onClick={() => applyBlurOnPreCallVideo()}
                    aria-label={!isVideoBlurred ? t('blur.turnOn') : t('blur.turnOff')}
                  >
                    <BlurSvg />
                  </button>
                </Right>
              </Controls>
            </VideoWrapper>
            <DeviceSelection>
              {availableDevices?.audioOutputs && (
                <DeviceDropdown
                  availableDevices={availableDevices.audioInputs}
                  selectedDevice={selectedDevices?.audioInput}
                  label={t('devices.microphone')}
                  onSelect={handleDeviceSelect}
                />
              )}
              {availableDevices?.audioOutputs && (
                <DeviceDropdown
                  availableDevices={availableDevices.audioOutputs}
                  selectedDevice={selectedDevices?.audioOutput}
                  label={t('devices.speaker')}
                  onSelect={handleDeviceSelect}
                />
              )}
              {availableDevices?.audioOutputs && (
                <DeviceDropdown
                  availableDevices={availableDevices.videoInputs}
                  selectedDevice={selectedDevices?.videoInput}
                  label={t('devices.camera')}
                  onSelect={handleDeviceSelect}
                />
              )}
            </DeviceSelection>
          </VideoWithControls>
          <Cards>
            <MeetingDetails>
              <Title>{t('preCall.meetingDetails')}</Title>
              <MeetingDate $state={meetState}>
                <div>{formatTime(new Date(meetInfo.startTime))}</div>
                <div>{formatDate(new Date(meetInfo.startTime))}</div>
              </MeetingDate>
              {meetState === 'ongoing' && (
                <Description $state={'ongoing'}>{t('preCall.ongoing')}</Description>
              )}
              {meetState === 'notStarted' && (
                <Description $state={'notStarted'}>
                  <ExclamationCircleFillSVG />
                  <div>
                    <span>{t('preCall.notStartedTitle')}</span>
                    <span>{t('preCall.notStartedDesc')}</span>
                  </div>
                </Description>
              )}
              {meetState === 'expired' && (
                <Description $state={'expired'}>
                  <ExclamationCircleFillSVG />
                  <div>
                    <span>{t('preCall.expiredTitle')}</span>
                    <span>{t('preCall.expiredDesc')}</span>
                  </div>
                </Description>
              )}

              <JoinMeeting
                $state={meetState}
                onClick={() => {
                  makeCall();
                  changeCurrentState('inCall');
                }}
                disabled={meetState !== 'ongoing'}
                aria-label={t('preCall.joinButton')}
              >
                {t('preCall.joinButton')}
              </JoinMeeting>
              {meetState !== 'expired' && <Illustration />}
            </MeetingDetails>
            <YourDetails>
              <Title>{t('preCall.yourDetails')}</Title>
              <Email>
                <EmailSvg /> {t('preCall.email')}
                <span>{meetInfo.visitorEmail}</span>
              </Email>
              <Name>
                {/*<NameSvg /> Name*/}
                {/*<span>{meetInfo.visitorName}</span>*/}
              </Name>
              <Phone>
                {/*<PhoneSvg /> Phone*/}
                {/*<span>{meetInfo.visitorPhone}</span>*/}
              </Phone>
            </YourDetails>
          </Cards>
        </Content>
      </Center>
      <PoweredBy>
        <a href={poweredByLink} target="_blank" aria-label={`${t('common.poweredBy')} ${t('common.opensInNewTab')}`}>
          {t('common.poweredBy')}
          <span className="sr-only">{t('common.opensInNewTab')}</span>
        </a>
      </PoweredBy>
    </PreCallWrapper>
  );
};
